import { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { usePostHog } from 'posthog-js/react';
import { identifyUser, resetAnalytics, useAccountCreationAttribution, type AccountAttributionClient } from '@lightning/analytics';
import { supabase } from '../services/supabase';
import { clearOnboardingDraft } from '../lib/onboardingDraft';
import { clearOnboardingStatusCache } from '../lib/onboardingStatus';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  useAccountCreationAttribution(supabase as unknown as AccountAttributionClient, user?.id, 'howie', user?.created_at);
  const recordedAppUsageForUser = useRef<string | null>(null);

  // Analytics: PostHog accessed via a ref so the auth subscription effect stays
  // stable (never re-created). identifyUser/aliasUser/reset must never throw
  // into auth flow, so each call site is guarded.
  const posthog = usePostHog();
  const posthogRef = useRef(posthog);
  useEffect(() => { posthogRef.current = posthog; }, [posthog]);
  const identifiedUserRef = useRef<string | null>(null);

  useEffect(() => {
    const recordAppUsage = (currentSession: Session | null) => {
      const currentUser = currentSession?.user;
      if (!currentUser || recordedAppUsageForUser.current === currentUser.id) return;

      recordedAppUsageForUser.current = currentUser.id;
      supabase
        .rpc('record_current_user_app_usage', {
          app_name: 'howie',
          source: 'app_visit',
        })
        .then(({ error }: { error: Error | null }) => {
          if (error) console.error('[Howie] Error recording app usage:', error);
        });
    };

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      recordAppUsage(session);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Analytics: identify on sign-in/sign-up, reset on sign-out.
      const ph = posthogRef.current;
      if (session?.user && ph) {
        const userId = session.user.id;
        if (identifiedUserRef.current !== userId) {
          identifyUser(userId, {
            email: session.user.email,
            name: session.user.user_metadata?.full_name || session.user.user_metadata?.name,
          }, ph);
          identifiedUserRef.current = userId;
        }
      }

      if (!session?.user && identifiedUserRef.current) {
        resetAnalytics(ph);
        identifiedUserRef.current = null;
      }

      if (session?.user) {
        recordAppUsage(session);
      } else {
        recordedAppUsageForUser.current = null;
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
  };

  const signOut = async () => {
    // Private reflections must not outlive the session on this device. The user in state can lag
    // the session (a sign-out fired before the first getSession result lands), so fall back to
    // the client's own session for the id.
    const userId = user?.id ?? (await supabase.auth.getSession()).data.session?.user?.id;
    if (userId) clearOnboardingDraft(userId);
    clearOnboardingStatusCache();
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
